import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { Turbo } from "@hotwired/turbo-rails"

// Drives the native Plaid Link SDK. On tap it fetches a server-minted
// link_token, hands it to the native PlaidLinkComponent, and on success posts
// the returned public_token back to Rails for exchange — then refreshes the
// screen. All HTTP stays here in the webview so the session cookie and the
// Plaid secrets never leave the server; native only opens the sheet and returns
// the one-time public_token.
export default class extends BridgeComponent {
  static component = "plaid-link"
  static targets = ["idle", "busy"]
  static values = {
    linkTokenUrl: String,
    exchangeUrl: String,
    // Update-mode completion URL. Set for BOTH update-mode flows — repairing a
    // login after ITEM_LOGIN_REQUIRED, and granting extra accounts after
    // NEW_ACCOUNTS_AVAILABLE. Either way the access_token is unchanged, so
    // success POSTs here instead of exchanging, and the server decides what
    // that means and where to send the user next.
    reauthorizedUrl: String
  }

  async open(event) {
    event?.preventDefault()
    // No native component connected (e.g. plain web browser) — do nothing.
    if (!this.enabled) return
    // Already opening or importing.
    if (this.element.disabled) return

    this.#setBusy(true)

    let linkToken
    try {
      linkToken = await this.#createLinkToken()
    } catch (error) {
      console.error("[plaid-link] link_token failed", error)
      this.#setBusy(false)
      return
    }

    this.send("open", { linkToken }, (message) => this.#handleResult(message))
  }

  async #createLinkToken() {
    const response = await fetch(this.linkTokenUrlValue, {
      method: "POST",
      headers: this.#headers()
    })
    if (!response.ok) throw new Error(`link_token ${response.status}`)
    const data = await response.json()
    if (!data.link_token) throw new Error("missing link_token")
    return data.link_token
  }

  #handleResult(message) {
    const { event, data } = message
    if (event === "success" && data?.publicToken) {
      if (this.hasReauthorizedUrlValue && this.reauthorizedUrlValue) {
        this.#reauthorized()
      } else {
        this.#exchange(data.publicToken, data.institution)
      }
    } else {
      // exit / cancelled / native error — restore the button, import nothing.
      this.#setBusy(false)
    }
  }

  // Update-mode success: tell Rails the item is healthy again, then refresh.
  async #reauthorized() {
    try {
      const response = await fetch(this.reauthorizedUrlValue, {
        method: "POST",
        headers: this.#headers()
      })
      if (!response.ok) throw new Error(`reauthorized ${response.status}`)
      const data = await response.json().catch(() => ({}))
      Turbo.visit(data.redirect || window.location.href, { action: "replace" })
    } catch (error) {
      console.error("[plaid-link] reauthorized failed", error)
      this.#setBusy(false)
    }
  }

  async #exchange(publicToken, institution) {
    this.#setLoading(true)
    try {
      const response = await fetch(this.exchangeUrlValue, {
        method: "POST",
        headers: this.#headers(),
        body: JSON.stringify({ public_token: publicToken, institution_name: institution })
      })
      if (!response.ok) throw new Error(`exchange ${response.status}`)
      // Server returns the first import to run from this screen, or (a
      // duplicate bank) where to go instead.
      const data = await response.json().catch(() => ({}))
      if (data.import) this.#import(data.import)
      else Turbo.visit(data.redirect || window.location.href, { action: "replace" })
    } catch (error) {
      console.error("[plaid-link] exchange failed", error)
      this.#setLoading(false)
      this.#setBusy(false)
    }
  }

  // PATCH assign_budget (the first import), submitted through Turbo so the
  // server's refresh_or_redirect_to closes the modal onto the dashboard.
  #import(url) {
    const form = document.createElement("form")
    form.method = "post"
    form.action = url
    form.hidden = true
    form.append(this.#hiddenInput("_method", "patch"))
    form.append(this.#hiddenInput("authenticity_token", this.#headers()["X-CSRF-Token"] || ""))
    form.addEventListener("turbo:submit-end", (event) => {
      if (event.detail?.success) return

      this.#setLoading(false)
      this.#setBusy(false)
    })
    document.body.append(form)
    form.requestSubmit()
  }

  #hiddenInput(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }

  // The cards in the middle of the bank screen swap for one spinner line;
  // the header and the Connect dock stay as they are.
  #setLoading(loading) {
    document.querySelectorAll("[data-plaid-cards]").forEach((el) => { el.hidden = loading })
    const loader = document.querySelector("[data-plaid-loading]")
    if (loader) loader.hidden = !loading
  }

  #headers() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
    }
  }

  // Loading state is scoped to the Connect button only: swap its label for a
  // spinner + "Connecting…" while we fetch the link_token and the native sheet
  // opens. Restored on exit/cancel/error; on success the page navigates away.
  #setBusy(busy) {
    this.element.disabled = busy
    this.element.classList.toggle("pointer-events-none", busy)
    this.element.classList.toggle("is-busy", busy)
    if (this.hasIdleTarget) this.idleTarget.style.display = busy ? "none" : "inline-flex"
    if (this.hasBusyTarget) this.busyTarget.style.display = busy ? "inline-flex" : "none"
    if (!this.hasBusyTarget && !this.hasIdleTarget) this.element.classList.toggle("opacity-60", busy)
  }
}
